import { ChangeDetectionStrategy, Component, OnChanges, OnDestroy } from '@angular/core';

import { ParticipantMarketStatus, ParticipantMarketType, ParticipantPriceStatus, ParticipantPriceType } from '@cds/betting-offer';
import { MarketHelpConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { DerivedMarkets, ForecastComponentMode, MarketView, WinGameFixture, WinGameParticipant } from '@frontend/sports/racing/feature/models';
import { clamp } from 'lodash-es';

import { HorsesFixtureHelper } from '../../../horse-racing-shared/services/horses-fixture-helper.service';
import { HorsesFixtureMarketHelper } from '../../../horse-racing-shared/services/horses-fixture-market-helper.service';
import { HorsesFixtureParticipantHelper } from '../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureTranslationHelper } from '../../../horse-racing-shared/services/horses-fixture-translation-helper.service';
import { EachWayMarketHelperService } from '../../../option-group/each-way-market-helper.service';
import { TabBarItem } from '../../../tab-bar/tab-bar.models';
import { GreyhoundTrackingService } from '../../common/greyhound-tracking.service';
import { RaceComponent } from '../greyhounds/greyhounds-race/race-base.component';

@Component({
    selector: 'ms-horses-race',
    templateUrl: 'horses-race.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HorsesRaceComponent extends RaceComponent implements OnChanges, OnDestroy {
    derivedMarkets = DerivedMarkets;
    trackingModuleType = '';
    trackingSource: string;
    readonly sportTracking = 'horseracing';

    constructor(
        protected override horsesFixturePricingHelper: HorsesFixturePricingHelper,
        public sitecore: Sitecore,
        protected override horsesFixtureMarketHelper: HorsesFixtureMarketHelper,
        protected override horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected override horsesFixtureHelper: HorsesFixtureHelper,
        protected override horsesFixtureTranslationHelper: HorsesFixtureTranslationHelper,
        protected override greyhoundTrackingService: GreyhoundTrackingService,
        protected override eachWayMarketHelperService: EachWayMarketHelperService,
        protected override marketHelpConfig: MarketHelpConfig,
        protected override urlHelper: UrlHelperService,
    ) {
        super(
            horsesFixtureMarketHelper,
            horsesFixtureTranslationHelper,
            eachWayMarketHelperService,
            horsesFixtureHelper,
            horsesFixtureParticipantHelper,
            horsesFixturePricingHelper,
            greyhoundTrackingService,
            marketHelpConfig,
            urlHelper,
        );
    }

    ngOnChanges(changes: ISimpleChanges<HorsesRaceComponent>): void {
        if (changes.fixture && changes.fixture.currentValue) {
            const resetSelectedMarket = changes.fixture.firstChange || changes.fixture.previousValue.id !== changes.fixture.currentValue.id;
            this.isOptionBet = changes.fixture.currentValue.isOptionBet;
            this.setViewModel(changes.fixture.currentValue, resetSelectedMarket);
        }
    }

    ngOnDestroy(): void {
        this.selectTopTwo.complete();
        this.selectTopThree.complete();
    }

    override onSwipe(direction: 'left' | 'right'): void {
        if (this.vm.selectedMarketView) {
            const increment = direction === 'right' ? -1 : 1;
            const crtMarketIdx = this.getMarketViewIndex(this.vm.selectedMarketView);
            if (crtMarketIdx === -1) {
                this.vm.selectedMarketView = this.getFirstMarketViewIfAny();
            }
            const newSelectedIdx = clamp(crtMarketIdx + increment, 0, this.vm.marketViews.length - 1);
            this.vm.selectedMarketView = this.vm.marketViews[newSelectedIdx]
                ? this.vm.marketViews[newSelectedIdx].view
                : this.getFirstMarketViewIfAny();
        }
    }

    setViewModel(fixture: WinGameFixture, resetSelectedMarket: boolean): void {
        this.marketTabs = this.horsesFixtureMarketHelper.getMarketsTabs(fixture);
        this.vm.timeformTipRunner = this.timeformParticipant(fixture);
        this.vm.prices = this.horsesFixturePricingHelper.getPrices(fixture, ParticipantMarketType.Win, ParticipantPriceType.Fixed);
        this.vm.marketViews = this.horsesFixtureMarketHelper.getMarkets(fixture);
        this.vm.canSelectTopForecast = this.canSelectTopParticipants(fixture, ParticipantMarketType.Forecast);
        this.vm.canSelectTopTricast = this.canSelectTopParticipants(fixture, ParticipantMarketType.Tricast);
        if (resetSelectedMarket) {
            // When switch to another fixture always select first available market.
            this.selectedMarketTitle = this.getFirstMarketTitleIfAny();
        } else {
            // When fixture is updated, keep current market selected.
            const currentSelectedMarket = this.selectedMarketTitle && this.marketTabs.find((t) => t.title === this.selectedMarketTitle);
            this.selectedMarketTitle = currentSelectedMarket ? currentSelectedMarket.title : this.getFirstMarketTitleIfAny();
        }
    }

    override setSelectedMarket(activeTabItem: string) {
        const activeTab = this.marketTabs.find((item) => item.id.toString() === activeTabItem);

        if (activeTab?.title) {
            this.selectedMarketTitle = activeTab.title;
            this.greyhoundTrackingService.trackselectMarket(this.fixture, activeTab.title);
        }
    }

    get active(): TabBarItem {
        return (this.marketTabs && this.marketTabs.filter((tab) => tab.active).pop()) ?? this.marketTabs[0];
    }

    private canSelectTopParticipants(fixture: WinGameFixture, marketType: ParticipantMarketType): boolean {
        const positions = marketType === ParticipantMarketType.Forecast ? [1, 2] : [1, 2, 3];

        return positions.every((pos) =>
            fixture.participants.some(
                (pr) =>
                    pr.timeformInfo?.analystRating === pos &&
                    pr.options.some(
                        (o) =>
                            o.marketType === marketType &&
                            o.marketStatus === ParticipantMarketStatus.Visible &&
                            o.participantPriceStatus === ParticipantPriceStatus.Visible,
                    ),
            ),
        );
    }

    protected timeformParticipant(fixture: WinGameFixture): WinGameParticipant | undefined {
        const participants = this.horsesFixtureParticipantHelper.getParticipants(fixture.participants);

        return participants.find((p) => !!(p.timeformInfo && p.timeformInfo.analystRating === 1));
    }

    isWinMarketSelected(selectedMarketTitle: string, market: string): boolean {
        const translatedWinMarketTitle = this.horsesFixtureTranslationHelper.translateMarketView(MarketView[market as keyof typeof MarketView]);

        return selectedMarketTitle === translatedWinMarketTitle;
    }

    getPickTrackingOptions(): { [key: string]: string } {
        const trackingSource = this.getPickTrackingSource(this.sportTracking);
        return this.greyhoundTrackingService.getAdditionalTrackingOptions(this.fixture, trackingSource);
    }

    protected readonly ForecastComponentMode = ForecastComponentMode;
}
